import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsAPI, usersAPI, teamsAPI, boqAPI, requisitionsAPI, subcontractorsAPI, jobSubcontractorsAPI, jobAssignmentsAPI, teamMembersAPI, assetsAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import LocationPicker from '../components/LocationPicker';
import GalleryModal from '../components/GalleryModal';
import ProgramModal from '../components/ProgramModal';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Coins,
  Upload,
  MessageSquare,
  Edit,
  Trash2,
  Package,
  FileText,
  Download,
  Eye,
  Printer,
  Save,
  X,
  User,
  Phone,
  Mail,
  Building2,
  Shield,
  Users,
  ClipboardList,
  CheckCircle2,
  Clock,
  HardHat,
  Plus,
  AlertTriangle,
  Star,
  Pencil,
  LayoutGrid,
  List,
  TrendingUp,
  TrendingDown,
  Banknote,
  PieChart,
  Image,
  Loader2,
  ClipboardCheck,
} from 'lucide-react';
import { format } from 'date-fns';
import { formatCurrency, formatPhoneNumber } from '../utils/currency';

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canViewJobCosts, isSuperAdmin, hasProjectPermission } = useAuth();
  const printRef = useRef();
  const attachmentInputRef = useRef();
  const [job, setJob] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [managers, setManagers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [boq, setBOQ] = useState(null);
  const [requisitions, setRequisitions] = useState([]);
  // Sub Contractors state
  const [jobSubcontractors, setJobSubcontractors] = useState([]);
  const [allSubcontractors, setAllSubcontractors] = useState([]);
  const [showSubcontractorModal, setShowSubcontractorModal] = useState(false);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState('');
  const [subcontractorContractValue, setSubcontractorContractValue] = useState('');
  const [uploadingSubDoc, setUploadingSubDoc] = useState(null);
  const [editingSubcontractorValue, setEditingSubcontractorValue] = useState(null);
  
  // Resource Assignments state
  const [resourceAssignments, setResourceAssignments] = useState([]);
  const [teamMembersList, setTeamMembersList] = useState([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState({
    member_id: '',
    assignment_start: '',
    assignment_start_time: '08:00',
    assignment_end: '',
    assignment_end_time: '17:00',
    planned_hours_per_day: 8,
    notes: '',
  });
  // Multi-select for team members in add mode
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  
  // Issued Assets state
  const [issuedAssetsDetails, setIssuedAssetsDetails] = useState([]);
  
  // Resource view toggle (list vs grid)
  const [resourceViewMode, setResourceViewMode] = useState(() => {
    return localStorage.getItem('resourceViewMode') || 'list';
  });

  // Gallery state
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  
  // Program Module state
  const [showProgramModal, setShowProgramModal] = useState(false);
  
  // Multi-file attachment upload state
  const [selectedAttachmentFiles, setSelectedAttachmentFiles] = useState([]);
  const [attachmentDescription, setAttachmentDescription] = useState('');

  // Access Model V2: super-admins pass; others gated by per-project CRUD flags.
  const canEdit = isSuperAdmin() || hasProjectPermission(id, 'can_edit');
  const canDelete = isSuperAdmin() || hasProjectPermission(id, 'can_delete');
  
  // Handle resource view mode change and persist to localStorage
  const handleResourceViewModeChange = (mode) => {
    setResourceViewMode(mode);
    localStorage.setItem('resourceViewMode', mode);
  };
  
  // Calculate job cost summary
  const calculateJobCostSummary = () => {
    // Labour cost from resource assignments
    const labourCost = resourceAssignments
      .filter(a => a.status !== 'cancelled')
      .reduce((sum, a) => sum + (a.total_planned_cost || 0), 0);
    
    // Material cost from BOQ (Materials category)
    const materialCost = boq?.line_items
      ?.filter(item => item.category === 'Materials')
      .reduce((sum, item) => sum + (item.amount || 0), 0) || 0;
    
    // Subcontractor cost from job subcontractors
    const subcontractorCost = jobSubcontractors
      .reduce((sum, js) => sum + (js.contract_value || 0), 0);
    
    // Total expenditure
    const totalExpenditure = labourCost + materialCost + subcontractorCost;
    
    // Job value (estimated or actual)
    const jobValue = job?.actual_cost || job?.estimated_cost || 0;
    
    // Variance
    const variance = jobValue - totalExpenditure;
    
    return {
      labourCost,
      materialCost,
      subcontractorCost,
      totalExpenditure,
      jobValue,
      variance,
      variancePercent: jobValue > 0 ? ((variance / jobValue) * 100).toFixed(1) : 0
    };
  };

  useEffect(() => {
    loadJobDetails();
    loadDropdownData();
  }, [id]);

  const loadJobDetails = async () => {
    try {
      const [jobRes, commentsRes, jobSubsRes, assignmentsRes] = await Promise.all([
        jobsAPI.getOne(id),
        jobsAPI.getComments(id),
        jobSubcontractorsAPI.getAll(id),
        jobAssignmentsAPI.getAll({ job_id: id }),
      ]);
      setJob(jobRes.data);
      setComments(commentsRes.data);
      setEditData(jobRes.data);
      setJobSubcontractors(jobSubsRes.data);
      setResourceAssignments(assignmentsRes.data || []);
      
      // Load BOQ if exists
      if (jobRes.data.boq_id) {
        try {
          const boqRes = await boqAPI.getOne(jobRes.data.boq_id);
          setBOQ(boqRes.data);
        } catch (e) {
          console.log('No BOQ found');
        }
      }
      
      // Load requisitions for this job
      try {
        const reqRes = await requisitionsAPI.getAll();
        const jobRequisitions = reqRes.data.filter(r => r.job_id === id);
        setRequisitions(jobRequisitions);
      } catch (e) {
        console.log('No requisitions found');
      }
      
      // Load issued assets details
      if (jobRes.data.issued_assets && jobRes.data.issued_assets.length > 0) {
        try {
          const assetPromises = jobRes.data.issued_assets.map(assetId => assetsAPI.getOne(assetId));
          const assetResults = await Promise.all(assetPromises);
          setIssuedAssetsDetails(assetResults.map(res => res.data));
        } catch (e) {
          console.log('Failed to load some asset details');
        }
      } else {
        setIssuedAssetsDetails([]);
      }
    } catch (error) {
      toast.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const loadDropdownData = async () => {
    try {
      const [managersRes, teamsRes, subsRes, membersRes] = await Promise.all([
        usersAPI.getManagers(),
        teamsAPI.getAll(),
        subcontractorsAPI.getAll(),
        teamMembersAPI.getAll(),
      ]);
      setManagers(managersRes.data);
      setTeams(teamsRes.data);
      setAllSubcontractors(subsRes.data);
      setTeamMembersList(membersRes.data.filter(m => m.status === 'active'));
    } catch (error) {
      console.error('Failed to load dropdown data');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await jobsAPI.update(id, { status: newStatus });
      setJob((prev) => ({ ...prev, status: newStatus }));
      toast.success('Job status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSaveEdit = async () => {
    try {
      const updateData = {
        title: editData.title,
        description: editData.description,
        priority: editData.priority,
        location: editData.location,
        job_type: editData.job_type,
        assigned_to: editData.assigned_to,
        assigned_team: editData.assigned_team,
        assigned_manager: editData.assigned_manager,
        client_name: editData.client_name,
        client_contact: editData.client_contact,
        client_alt_contact: editData.client_alt_contact,
        client_email: editData.client_email,
        estimated_cost: editData.estimated_cost || 0,
        actual_cost: editData.actual_cost || 0,
        subcontractor_value: editData.subcontractor_value || 0,
        scheduled_date: editData.scheduled_date || null,
        estimated_completion_date: editData.estimated_completion_date || null,
        // Geofence fields
        geofence_latitude: editData.geofence_latitude || null,
        geofence_longitude: editData.geofence_longitude || null,
        geofence_radius: editData.geofence_radius || 100,
      };

      if (editData.job_type === 'insurance') {
        Object.assign(updateData, {
          insurer_name: editData.insurer_name,
          insurer_contact_person: editData.insurer_contact_person,
          insurer_email: editData.insurer_email,
          insurer_contact_no: editData.insurer_contact_no,
          insurer_reference_no: editData.insurer_reference_no,
          insurer_claim_no: editData.insurer_claim_no,
          loss_adjuster_name: editData.loss_adjuster_name,
          loss_adjuster_contact: editData.loss_adjuster_contact,
          loss_adjuster_contact_no: editData.loss_adjuster_contact_no,
          loss_adjuster_email: editData.loss_adjuster_email,
        });
      }

      await jobsAPI.update(id, updateData);
      setJob({ ...job, ...updateData });
      setEditing(false);
      toast.success('Job updated successfully');
    } catch (error) {
      toast.error('Failed to update job');
    }
  };

  // Handle multi-file attachment selection
  const handleAttachmentFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedAttachmentFiles(files);
  };

  // Handle multi-file attachment upload
  const handleMultiFileUpload = async () => {
    if (selectedAttachmentFiles.length === 0) return;

    setUploading(true);
    try {
      await jobsAPI.uploadMultipleAttachments(id, selectedAttachmentFiles, attachmentDescription);
      toast.success(`${selectedAttachmentFiles.length} file(s) uploaded successfully`);
      setSelectedAttachmentFiles([]);
      setAttachmentDescription('');
      if (attachmentInputRef.current) attachmentInputRef.current.value = '';
      loadJobDetails();
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  // Handle single file upload (legacy support)
  const handleFileUpload = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setUploading(true);
    try {
      await jobsAPI.uploadAttachment(id, selectedFile);
      toast.success('File uploaded successfully');
      loadJobDetails();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  // Handle attachment deletion
  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Delete this attachment?')) return;
    
    try {
      await jobsAPI.deleteAttachment(id, attachmentId);
      toast.success('Attachment deleted');
      loadJobDetails();
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const handleDownloadAttachment = (attachment) => {
    try {
      const byteCharacters = atob(attachment.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.content_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleViewAttachment = (attachment) => {
    try {
      const byteCharacters = atob(attachment.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: attachment.content_type });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to view file');
    }
  };

  const handlePrint = () => {
    const printContent = document.getElementById('print-content');
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Job Report - ${job.job_code || ''} ${job.title}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #1e293b; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
            h2 { color: #334155; margin-top: 20px; }
            .job-code { font-family: monospace; font-size: 14px; color: #2563eb; background: #eff6ff; padding: 4px 8px; border-radius: 4px; margin-bottom: 8px; display: inline-block; }
            .section { margin-bottom: 20px; padding: 15px; border: 1px solid #e2e8f0; border-radius: 8px; }
            .field { margin-bottom: 8px; }
            .label { font-weight: bold; color: #64748b; }
            .value { color: #1e293b; }
            .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; }
            .priority-urgent { background: #fee2e2; color: #991b1b; }
            .priority-high { background: #ffedd5; color: #c2410c; }
            .priority-medium { background: #fef9c3; color: #a16207; }
            .priority-low { background: #dcfce7; color: #166534; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { border: 1px solid #e2e8f0; padding: 8px; text-align: left; }
            th { background: #f1f5f9; }
            @media print { .no-print { display: none; } }
          </style>
        </head>
        <body>
          <div style="text-align: center; margin-bottom: 20px; border-bottom: 2px solid #e2e8f0; padding-bottom: 20px;">
            <img src="https://customer-assets.emergentagent.com/job_lbr-build/artifacts/t9f432h8_MB%20Con%20Logo.png" alt="MBConstruction" style="max-width: 300px; height: auto; margin-bottom: 10px;" />
          </div>
          <h1>Job Report</h1>
          <div class="section">
            <h2>Job Information</h2>
            ${job.job_code ? `<div class="job-code">${job.job_code}</div>` : ''}
            <div class="field"><span class="label">Title:</span> <span class="value">${job.title}</span></div>
            <div class="field"><span class="label">Status:</span> <span class="value">${job.status.replace('_', ' ')}</span></div>
            <div class="field"><span class="label">Priority:</span> <span class="badge priority-${job.priority}">${job.priority}</span></div>
            <div class="field"><span class="label">Job Type:</span> <span class="value">${job.job_type}</span></div>
            <div class="field"><span class="label">Location:</span> <span class="value">${job.location}</span></div>
            ${job.scheduled_date ? `<div class="field"><span class="label">Scheduled Date:</span> <span class="value">${format(new Date(job.scheduled_date), 'MMM dd, yyyy hh:mm a')}</span></div>` : ''}
            ${job.estimated_completion_date ? `<div class="field"><span class="label">Est. Completion:</span> <span class="value">${format(new Date(job.estimated_completion_date), 'MMM dd, yyyy')}</span></div>` : ''}
            <div class="field"><span class="label">Description:</span></div>
            <p class="value">${job.description}</p>
          </div>

          <div class="section">
            <h2>Client Information</h2>
            <div class="field"><span class="label">Client Name:</span> <span class="value">${job.client_name || 'N/A'}</span></div>
            <div class="field"><span class="label">Contact:</span> <span class="value">${job.client_contact || 'N/A'}</span></div>
            <div class="field"><span class="label">Alt. Contact:</span> <span class="value">${job.client_alt_contact || 'N/A'}</span></div>
            <div class="field"><span class="label">Email:</span> <span class="value">${job.client_email || 'N/A'}</span></div>
          </div>

          ${job.job_type === 'insurance' ? `
          <div class="section">
            <h2>Insurance Information</h2>
            <div class="field"><span class="label">Insurer:</span> <span class="value">${job.insurer_name || 'N/A'}</span></div>
            <div class="field"><span class="label">Contact Person:</span> <span class="value">${job.insurer_contact_person || 'N/A'}</span></div>
            <div class="field"><span class="label">Reference No:</span> <span class="value">${job.insurer_reference_no || 'N/A'}</span></div>
            <div class="field"><span class="label">Claim No:</span> <span class="value">${job.insurer_claim_no || 'N/A'}</span></div>
            <h3>Loss Adjuster</h3>
            <div class="field"><span class="label">Name:</span> <span class="value">${job.loss_adjuster_name || 'N/A'}</span></div>
            <div class="field"><span class="label">Contact:</span> <span class="value">${job.loss_adjuster_contact_no || 'N/A'}</span></div>
          </div>
          ` : ''}

          <div class="section">
            <h2>Assignment</h2>
            <div class="field"><span class="label">Manager/Supervisor:</span> <span class="value">${managers.find(m => m.id === job.assigned_manager)?.name || 'Not assigned'}</span></div>
            <div class="field"><span class="label">Team:</span> <span class="value">${teams.find(t => t.id === job.assigned_team)?.name || 'Not assigned'}</span></div>
          </div>

          ${boq ? `
          <div class="section">
            <h2>Bill of Quantities</h2>
            <table>
              <thead>
                <tr><th>Item</th><th>Qty</th><th>Unit</th><th>Rate</th><th>Amount</th></tr>
              </thead>
              <tbody>
                ${boq.line_items?.map(item => `
                  <tr>
                    <td>${item.item_description}</td>
                    <td>${item.quantity}</td>
                    <td>${item.unit}</td>
                    <td>R ${item.rate.toFixed(2)}</td>
                    <td>R ${item.amount.toFixed(2)}</td>
                  </tr>
                `).join('') || '<tr><td colspan="5">No items</td></tr>'}
              </tbody>
              <tfoot>
                <tr><th colspan="4">Total</th><th>R ${boq.total_amount?.toFixed(2) || '0.00'}</th></tr>
              </tfoot>
            </table>
          </div>
          ` : ''}

          ${requisitions.length > 0 ? `
          <div class="section">
            <h2>Requisitions</h2>
            ${requisitions.map(req => `
              <div style="margin-bottom: 10px; padding: 10px; background: #f8fafc; border-radius: 4px;">
                <div class="field"><span class="label">Status:</span> <span class="value">${req.status}</span></div>
                <div class="field"><span class="label">Items:</span> <span class="value">${req.items?.length || 0} items</span></div>
              </div>
            `).join('')}
          </div>
          ` : ''}

          ${job.attachments?.length > 0 ? `
          <div class="section">
            <h2>Attachments</h2>
            <ul>
              ${job.attachments.map(att => `<li>${att.filename}</li>`).join('')}
            </ul>
          </div>
          ` : ''}

          <div style="margin-top: 30px; text-align: center; color: #64748b; font-size: 12px;">
            <p>Printed on ${format(new Date(), 'MMM dd, yyyy hh:mm a')}</p>
            <p>MBConstruction (Pty) Ltd - Maintenance Management System</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await jobsAPI.addComment(id, newComment);
      setNewComment('');
      loadJobDetails();
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;

    try {
      await jobsAPI.delete(id);
      toast.success('Job deleted successfully');
      navigate('/jobs');
    } catch (error) {
      toast.error('Failed to delete job');
    }
  };

  // Sub Contractor handlers
  const handleAssignSubcontractor = async () => {
    if (!selectedSubcontractor) {
      toast.error('Please select a sub contractor');
      return;
    }
    
    try {
      await jobSubcontractorsAPI.assign(id, { 
        subcontractor_id: selectedSubcontractor,
        contract_value: subcontractorContractValue ? parseFloat(subcontractorContractValue) : 0
      });
      toast.success('Sub contractor assigned successfully');
      setShowSubcontractorModal(false);
      setSelectedSubcontractor('');
      setSubcontractorContractValue('');
      loadJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign sub contractor');
    }
  };

  const handleUpdateSubcontractorValue = async (assignmentId, newValue) => {
    try {
      await jobSubcontractorsAPI.update(id, assignmentId, { 
        contract_value: parseFloat(newValue) || 0 
      });
      toast.success('Contract value updated');
      setEditingSubcontractorValue(null);
      loadJobDetails();
    } catch (error) {
      toast.error('Failed to update contract value');
    }
  };

  const handleRemoveSubcontractor = async (assignmentId) => {
    if (!window.confirm('Remove this sub contractor from the job?')) return;
    
    try {
      await jobSubcontractorsAPI.remove(id, assignmentId);
      toast.success('Sub contractor removed');
      loadJobDetails();
    } catch (error) {
      toast.error('Failed to remove sub contractor');
    }
  };

  const handleUploadQuote = async (assignmentId, file) => {
    setUploadingSubDoc(assignmentId + '-quote');
    try {
      await jobSubcontractorsAPI.uploadQuote(id, assignmentId, file);
      toast.success('Quote uploaded successfully');
      loadJobDetails();
    } catch (error) {
      toast.error('Failed to upload quote');
    } finally {
      setUploadingSubDoc(null);
    }
  };

  const handleUploadAppointmentLetter = async (assignmentId, file) => {
    setUploadingSubDoc(assignmentId + '-letter');
    try {
      await jobSubcontractorsAPI.uploadAppointmentLetter(id, assignmentId, file);
      toast.success('Appointment letter uploaded successfully');
      loadJobDetails();
    } catch (error) {
      toast.error('Failed to upload appointment letter');
    } finally {
      setUploadingSubDoc(null);
    }
  };

  const handleViewSubDoc = (doc) => {
    try {
      const byteCharacters = atob(doc.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: doc.content_type });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to view document');
    }
  };

  // Resource Assignment handlers
  const openAssignmentModal = (assignment = null) => {
    if (assignment) {
      setEditingAssignment(assignment);
      // Parse existing datetime into date and time components
      const startDate = assignment.assignment_start ? new Date(assignment.assignment_start) : null;
      const endDate = assignment.assignment_end ? new Date(assignment.assignment_end) : null;
      
      setAssignmentForm({
        member_id: assignment.member_id,
        assignment_start: startDate ? format(startDate, 'yyyy-MM-dd') : '',
        assignment_start_time: startDate ? format(startDate, 'HH:mm') : '08:00',
        assignment_end: endDate ? format(endDate, 'yyyy-MM-dd') : '',
        assignment_end_time: endDate ? format(endDate, 'HH:mm') : '17:00',
        planned_hours_per_day: assignment.planned_hours_per_day || 8,
        notes: assignment.notes || '',
      });
      setSelectedMemberIds([]);
    } else {
      setEditingAssignment(null);
      setAssignmentForm({
        member_id: '',
        assignment_start: '',
        assignment_start_time: '08:00',
        assignment_end: '',
        assignment_end_time: '17:00',
        planned_hours_per_day: 8,
        notes: '',
      });
      setSelectedMemberIds([]);
    }
    setShowAssignmentModal(true);
  };
  
  // Toggle member selection for multi-select
  const toggleMemberSelection = (memberId) => {
    setSelectedMemberIds(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  const handleSaveAssignment = async () => {
    // For editing, use single member_id; for creating, use selectedMemberIds
    const memberIds = editingAssignment ? [assignmentForm.member_id] : selectedMemberIds;
    
    if (memberIds.length === 0 || !assignmentForm.assignment_start || !assignmentForm.assignment_end) {
      toast.error('Please select at least one team member and fill in all required fields');
      return;
    }
    
    try {
      // Combine date and time for proper datetime
      const startDateTime = `${assignmentForm.assignment_start}T${assignmentForm.assignment_start_time || '08:00'}:00`;
      const endDateTime = `${assignmentForm.assignment_end}T${assignmentForm.assignment_end_time || '17:00'}:00`;
      
      if (editingAssignment) {
        // Single update for editing
        const data = {
          member_id: assignmentForm.member_id,
          assignment_start: startDateTime,
          assignment_end: endDateTime,
          planned_hours_per_day: assignmentForm.planned_hours_per_day,
          notes: assignmentForm.notes,
          job_id: id,
        };
        await jobAssignmentsAPI.update(editingAssignment.id, data);
        toast.success('Assignment updated successfully');
      } else {
        // Multi-create for new assignments
        const successfulMembers = [];
        const conflictingMembers = [];
        const alreadyAssignedMembers = [];
        
        for (const memberId of memberIds) {
          const member = teamMembersList.find(m => m.id === memberId);
          const memberName = member?.name || 'Unknown';
          
          const data = {
            member_id: memberId,
            assignment_start: startDateTime,
            assignment_end: endDateTime,
            planned_hours_per_day: assignmentForm.planned_hours_per_day,
            notes: assignmentForm.notes,
            job_id: id,
          };
          
          try {
            // Check for conflicts
            const conflictRes = await jobAssignmentsAPI.checkConflicts(data);
            
            // Skip if already assigned to THIS job for overlapping dates
            if (conflictRes.data?.already_assigned) {
              alreadyAssignedMembers.push(memberName);
              continue;
            }
            
            // Block if has conflicts on OTHER jobs
            if (conflictRes.data?.has_conflicts) {
              const conflictJobs = conflictRes.data.conflicts?.map(c => c.job_code || c.job_title).join(', ');
              conflictingMembers.push(`${memberName} (${conflictJobs})`);
              continue;
            }
          } catch (e) {
            // If conflict check fails, proceed anyway
            console.log('Conflict check failed for', memberName);
          }
          
          try {
            await jobAssignmentsAPI.create(data);
            successfulMembers.push(memberName);
          } catch (createError) {
            conflictingMembers.push(memberName);
          }
        }
        
        // Show results
        if (successfulMembers.length > 0) {
          toast.success(`Successfully assigned: ${successfulMembers.join(', ')}`);
        }
        if (alreadyAssignedMembers.length > 0) {
          toast.info(`Already assigned to this job: ${alreadyAssignedMembers.join(', ')}`);
        }
        if (conflictingMembers.length > 0) {
          toast.error(`Conflicts on other jobs: ${conflictingMembers.join(', ')}`);
        }
      }
      
      setShowAssignmentModal(false);
      setAssignmentForm({
        member_id: '',
        assignment_start: '',
        assignment_start_time: '08:00',
        assignment_end: '',
        assignment_end_time: '17:00',
        planned_hours_per_day: 8,
        notes: '',
      });
      setSelectedMemberIds([]);
      setEditingAssignment(null);
      loadJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save assignment');
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Remove this resource assignment?')) return;
    
    try {
      await jobAssignmentsAPI.delete(assignmentId);
      toast.success('Assignment removed');
      loadJobDetails();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(10)].map((_, i) => (
          <Star
            key={i}
            className={`w-2.5 h-2.5 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`}
          />
        ))}
      </div>
    );
  };

  const priorityColors = {
    urgent: 'bg-red-500 text-white border-red-600',
    high: 'bg-orange-500 text-white border-orange-600',
    medium: 'bg-yellow-500 text-white border-yellow-600',
    low: 'bg-green-500 text-white border-green-600',
  };

  const statusColors = {
    pending: 'bg-slate-100 text-slate-700 border-slate-200',
    in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
    completed: 'bg-green-100 text-green-800 border-green-200',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="p-6">
        <p className="text-slate-600">Job not found</p>
      </div>
    );
  }

  return (
    <div data-testid="job-detail-page" className="p-6">
      <div className="flex items-center justify-between mb-6">
        <button
          data-testid="back-button"
          onClick={() => navigate('/jobs')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Jobs
        </button>
        <div className="flex gap-2">
          <button
            onClick={() => setShowProgramModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
            data-testid="program-module-button"
          >
            <ClipboardCheck className="w-4 h-4" />
            Program
          </button>
          <button
            onClick={() => setShowGalleryModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
            data-testid="gallery-button"
          >
            <Image className="w-4 h-4" />
            Gallery
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
          {canEdit && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Edit className="w-4 h-4" />
              Edit Job
            </button>
          )}
          {editing && (
            <>
              <button
                onClick={handleSaveEdit}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
              <button
                onClick={() => { setEditing(false); setEditData(job); }}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Job Header */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-8">
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                {/* Job Code Badge */}
                {job.job_code && (
                  <div className="mb-2">
                    <span className="text-sm font-mono font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
                      {job.job_code}
                    </span>
                  </div>
                )}
                {editing ? (
                  <input
                    type="text"
                    value={editData.title}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                    className="text-3xl font-bold text-slate-900 w-full border-b-2 border-blue-500 focus:outline-none pb-1 mb-3"
                  />
                ) : (
                  <h1 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {job.title}
                  </h1>
                )}
                <div className="flex flex-wrap gap-2">
                  {editing ? (
                    <select
                      value={editData.priority}
                      onChange={(e) => setEditData({ ...editData, priority: e.target.value })}
                      className="px-3 py-1 rounded-full text-sm font-medium border"
                    >
                      <option value="low">Low Priority</option>
                      <option value="medium">Medium Priority</option>
                      <option value="high">High Priority</option>
                      <option value="urgent">Urgent Priority</option>
                    </select>
                  ) : (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${priorityColors[job.priority]}`}>
                      {job.priority} priority
                    </span>
                  )}
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusColors[job.status]}`}>
                    {job.status.replace('_', ' ')}
                  </span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${job.job_type === 'insurance' ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                    {job.job_type}
                  </span>
                </div>
              </div>
              {canDelete && (
                <button
                  data-testid="delete-button"
                  onClick={handleDelete}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 text-slate-600">
                <MapPin className="w-5 h-5 flex-shrink-0" />
                {editing ? (
                  <input
                    type="text"
                    value={editData.location}
                    onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                    className="flex-1 px-2 py-1 border rounded"
                  />
                ) : (
                  <span>{job.location}</span>
                )}
              </div>
              {(job.scheduled_date || editing) && (
                <div className="flex items-center gap-3 text-slate-600">
                  <Calendar className="w-5 h-5 flex-shrink-0" />
                  {editing ? (
                    <input
                      type="datetime-local"
                      value={editData.scheduled_date ? editData.scheduled_date.slice(0, 16) : ''}
                      onChange={(e) => setEditData({ ...editData, scheduled_date: e.target.value })}
                      className="flex-1 px-2 py-1 border rounded"
                    />
                  ) : (
                    <span>Scheduled: {format(new Date(job.scheduled_date), 'MMM dd, yyyy hh:mm a')}</span>
                  )}
                </div>
              )}
              {(job.estimated_completion_date || editing) && (
                <div className="flex items-center gap-3 text-slate-600">
                  <Clock className="w-5 h-5 flex-shrink-0" />
                  {editing ? (
                    <input
                      type="datetime-local"
                      value={editData.estimated_completion_date ? editData.estimated_completion_date.slice(0, 16) : ''}
                      onChange={(e) => setEditData({ ...editData, estimated_completion_date: e.target.value })}
                      className="flex-1 px-2 py-1 border rounded"
                    />
                  ) : (
                    <span>Est. Completion: {format(new Date(job.estimated_completion_date), 'MMM dd, yyyy')}</span>
                  )}
                </div>
              )}
              {canEdit && (
                <div className="flex items-start gap-3 text-slate-600">
                  <Coins className="w-5 h-5 flex-shrink-0 mt-1" />
                  {editing ? (
                    <div className="flex flex-wrap gap-2 flex-1">
                      <input
                        type="text"
                        value={editData.estimated_cost_str !== undefined ? editData.estimated_cost_str : (editData.estimated_cost || '')}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          setEditData({ 
                            ...editData, 
                            estimated_cost_str: value,
                            estimated_cost: value ? parseFloat(value) : 0 
                          });
                        }}
                        className="w-32 px-2 py-1 border rounded text-sm"
                        placeholder="Est. Cost"
                      />
                      <input
                        type="text"
                        value={editData.actual_cost_str !== undefined ? editData.actual_cost_str : (editData.actual_cost || '')}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9.]/g, '');
                          setEditData({ 
                            ...editData, 
                            actual_cost_str: value,
                            actual_cost: value ? parseFloat(value) : 0 
                          });
                        }}
                        className="w-32 px-2 py-1 border rounded text-sm"
                        placeholder="Actual Cost"
                      />
                    </div>
                  ) : (
                    <span>
                      Estimated: {formatCurrency(job.estimated_cost || 0)} {job.actual_cost > 0 && `| Actual: ${formatCurrency(job.actual_cost)}`}
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-slate-200 pt-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Description
              </h2>
              {editing ? (
                <textarea
                  value={editData.description}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              ) : (
                <p className="text-slate-700 whitespace-pre-wrap">{job.description}</p>
              )}
            </div>

            {/* Geofence Settings */}
            {editing && (
              <div className="border-t border-slate-200 pt-6 mt-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <MapPin className="w-5 h-5" />
                  Geofence Settings
                </h2>
                <p className="text-sm text-slate-500 mb-4">Set location for clock in/out verification</p>
                <LocationPicker
                  latitude={editData.geofence_latitude}
                  longitude={editData.geofence_longitude}
                  radius={editData.geofence_radius || 100}
                  onLocationChange={(lat, lng) => setEditData({ 
                    ...editData, 
                    geofence_latitude: lat, 
                    geofence_longitude: lng 
                  })}
                  onRadiusChange={(r) => setEditData({ ...editData, geofence_radius: r })}
                />
              </div>
            )}
            {!editing && job.geofence_latitude && job.geofence_longitude && (
              <div className="border-t border-slate-200 pt-6 mt-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <MapPin className="w-5 h-5" />
                  Geofence
                </h2>
                <p className="text-sm text-slate-600">
                  Location: {job.geofence_latitude.toFixed(6)}, {job.geofence_longitude.toFixed(6)} 
                  <span className="ml-2 text-slate-400">(radius: {job.geofence_radius || 100}m)</span>
                </p>
              </div>
            )}
          </div>

          {/* Job Cost Summary - Only show if user has financial access */}
          {canEdit && canViewJobCosts() && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6" data-testid="job-cost-summary">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-blue-600" />
                Job Cost Summary
              </h2>
              {(() => {
                const costs = calculateJobCostSummary();
                return (
                  <div className="space-y-4">
                    {/* Job Value */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-blue-600 font-medium">Job Value</p>
                          <p className="text-xs text-blue-500">
                            {job?.actual_cost > 0 ? 'Actual Cost' : 'Estimated Cost'}
                          </p>
                        </div>
                        <p className="text-2xl font-bold text-blue-700">{formatCurrency(costs.jobValue)}</p>
                      </div>
                    </div>
                    
                    {/* Expenditure Breakdown */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Users className="w-4 h-4 text-slate-500" />
                          <p className="text-xs text-slate-500 font-medium">Labour</p>
                        </div>
                        <p className="text-lg font-semibold text-slate-900">{formatCurrency(costs.labourCost)}</p>
                        <p className="text-xs text-slate-400">{resourceAssignments.filter(a => a.status !== 'cancelled').length} assignments</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Package className="w-4 h-4 text-slate-500" />
                          <p className="text-xs text-slate-500 font-medium">Materials</p>
                        </div>
                        <p className="text-lg font-semibold text-slate-900">{formatCurrency(costs.materialCost)}</p>
                        <p className="text-xs text-slate-400">From BOQ</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                        <div className="flex items-center gap-2 mb-1">
                          <HardHat className="w-4 h-4 text-slate-500" />
                          <p className="text-xs text-slate-500 font-medium">Subcontractors</p>
                        </div>
                        <p className="text-lg font-semibold text-slate-900">{formatCurrency(costs.subcontractorCost)}</p>
                        <p className="text-xs text-slate-400">{jobSubcontractors.length} subcontractors</p>
                      </div>
                    </div>
                    
                    {/* Total and Variance */}
                    <div className="border-t border-slate-200 pt-4 mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Total Expenditure</span>
                        <span className="text-lg font-bold text-slate-900">{formatCurrency(costs.totalExpenditure)}</span>
                      </div>
                      <div className={`flex items-center justify-between p-3 rounded-lg ${
                        costs.variance >= 0 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex items-center gap-2">
                          {costs.variance >= 0 ? (
                            <TrendingUp className="w-5 h-5 text-green-600" />
                          ) : (
                            <TrendingDown className="w-5 h-5 text-red-600" />
                          )}
                          <span className={`text-sm font-medium ${costs.variance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {costs.variance >= 0 ? 'Under Budget' : 'Over Budget'}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className={`text-lg font-bold ${costs.variance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {costs.variance >= 0 ? '+' : ''}{formatCurrency(costs.variance)}
                          </p>
                          <p className={`text-xs ${costs.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {costs.variancePercent}% of job value
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Client Information */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Client Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-500">Client Name</label>
                {editing ? (
                  <input
                    type="text"
                    value={editData.client_name || ''}
                    onChange={(e) => setEditData({ ...editData, client_name: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg mt-1"
                  />
                ) : (
                  <p className="text-slate-900">{job.client_name || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-slate-500">Email</label>
                {editing ? (
                  <input
                    type="email"
                    value={editData.client_email || ''}
                    onChange={(e) => setEditData({ ...editData, client_email: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg mt-1"
                  />
                ) : (
                  <p className="text-slate-900">{job.client_email || 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-slate-500">Contact</label>
                {editing ? (
                  <input
                    type="tel"
                    value={editData.client_contact || ''}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      setEditData({ ...editData, client_contact: formatted });
                    }}
                    className="w-full px-3 py-2 border rounded-lg mt-1"
                    placeholder="+27 21 123 4567"
                  />
                ) : (
                  <p className="text-slate-900">{job.client_contact ? formatPhoneNumber(job.client_contact) : 'N/A'}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-slate-500">Alternate Contact</label>
                {editing ? (
                  <input
                    type="tel"
                    value={editData.client_alt_contact || ''}
                    onChange={(e) => {
                      const formatted = formatPhoneNumber(e.target.value);
                      setEditData({ ...editData, client_alt_contact: formatted });
                    }}
                    className="w-full px-3 py-2 border rounded-lg mt-1"
                    placeholder="+27 21 123 4567"
                  />
                ) : (
                  <p className="text-slate-900">{job.client_alt_contact ? formatPhoneNumber(job.client_alt_contact) : 'N/A'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Insurance Information (conditional) */}
          {(job.job_type === 'insurance' || (editing && editData.job_type === 'insurance')) && (
            <div className="bg-purple-50 border border-purple-200 shadow-sm rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                Insurance Information
              </h2>
              {editing && (
                <div className="mb-4">
                  <label className="text-sm text-slate-500">Job Type</label>
                  <select
                    value={editData.job_type}
                    onChange={(e) => setEditData({ ...editData, job_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg mt-1"
                  >
                    <option value="private">Private</option>
                    <option value="insurance">Insurance</option>
                  </select>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="text-sm text-slate-500">Insurer Name</label>
                  {editing ? (
                    <input type="text" value={editData.insurer_name || ''} onChange={(e) => setEditData({ ...editData, insurer_name: e.target.value })} className="w-full px-3 py-2 border rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{job.insurer_name || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-500">Contact Person</label>
                  {editing ? (
                    <input type="text" value={editData.insurer_contact_person || ''} onChange={(e) => setEditData({ ...editData, insurer_contact_person: e.target.value })} className="w-full px-3 py-2 border rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{job.insurer_contact_person || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-500">Reference No</label>
                  {editing ? (
                    <input type="text" value={editData.insurer_reference_no || ''} onChange={(e) => setEditData({ ...editData, insurer_reference_no: e.target.value })} className="w-full px-3 py-2 border rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{job.insurer_reference_no || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-500">Claim No</label>
                  {editing ? (
                    <input type="text" value={editData.insurer_claim_no || ''} onChange={(e) => setEditData({ ...editData, insurer_claim_no: e.target.value })} className="w-full px-3 py-2 border rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{job.insurer_claim_no || 'N/A'}</p>
                  )}
                </div>
              </div>
              <h3 className="text-md font-semibold text-slate-900 mb-3">Loss Adjuster</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-slate-500">Name</label>
                  {editing ? (
                    <input type="text" value={editData.loss_adjuster_name || ''} onChange={(e) => setEditData({ ...editData, loss_adjuster_name: e.target.value })} className="w-full px-3 py-2 border rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{job.loss_adjuster_name || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-500">Contact No</label>
                  {editing ? (
                    <input type="tel" value={editData.loss_adjuster_contact_no || ''} onChange={(e) => setEditData({ ...editData, loss_adjuster_contact_no: e.target.value })} className="w-full px-3 py-2 border rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{job.loss_adjuster_contact_no || 'N/A'}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-slate-500">Email</label>
                  {editing ? (
                    <input type="email" value={editData.loss_adjuster_email || ''} onChange={(e) => setEditData({ ...editData, loss_adjuster_email: e.target.value })} className="w-full px-3 py-2 border rounded-lg mt-1" />
                  ) : (
                    <p className="text-slate-900">{job.loss_adjuster_email || 'N/A'}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Assignment Section */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Assignment
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-sm text-slate-500">Manager/Supervisor</label>
                {editing ? (
                  <select
                    value={editData.assigned_manager || ''}
                    onChange={(e) => setEditData({ ...editData, assigned_manager: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg mt-1"
                  >
                    <option value="">Not assigned</option>
                    {managers.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-slate-900">{managers.find(m => m.id === job.assigned_manager)?.name || 'Not assigned'}</p>
                )}
              </div>
              <div>
                <label className="text-sm text-slate-500">Team</label>
                {editing ? (
                  <select
                    value={editData.assigned_team || ''}
                    onChange={(e) => setEditData({ ...editData, assigned_team: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg mt-1"
                  >
                    <option value="">Not assigned</option>
                    {teams.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                ) : (
                  <p className="text-slate-900">{teams.find(t => t.id === job.assigned_team)?.name || 'Not assigned'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Resource Assignments Section - Time-bounded assignments */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Resource Assignments
              </h2>
              <div className="flex items-center gap-2">
                {/* List/Grid Toggle for assignments display */}
                {resourceAssignments.length > 0 && (
                  <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg mr-2">
                    <button
                      onClick={() => handleResourceViewModeChange('list')}
                      data-testid="assignments-view-list-btn"
                      className={`p-1.5 rounded transition-colors ${
                        resourceViewMode === 'list' 
                          ? 'bg-white text-blue-600 shadow-sm' 
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                      title="List view"
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleResourceViewModeChange('grid')}
                      data-testid="assignments-view-grid-btn"
                      className={`p-1.5 rounded transition-colors ${
                        resourceViewMode === 'grid' 
                          ? 'bg-white text-blue-600 shadow-sm' 
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                      title="Grid view"
                    >
                      <LayoutGrid className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {canEdit && (
                  <button
                    onClick={() => openAssignmentModal()}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                )}
              </div>
            </div>
            <p className="text-sm text-slate-500 mb-4">
              Assign team members for specific time periods with planned hours.
            </p>
            
            {resourceAssignments.length > 0 ? (
              resourceViewMode === 'list' ? (
                /* Excel-style List View for Assignments */
                <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="assignments-list-view">
                  {/* Header Row */}
                  <div className="bg-slate-100 border-b border-slate-300 grid grid-cols-12 gap-0 text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    <div className="col-span-2 px-3 py-2 border-r border-slate-200">Resource</div>
                    <div className="col-span-1 px-2 py-2 border-r border-slate-200 text-center">Status</div>
                    <div className="col-span-2 px-2 py-2 border-r border-slate-200 text-center">Start Date</div>
                    <div className="col-span-2 px-2 py-2 border-r border-slate-200 text-center">End Date</div>
                    <div className="col-span-1 px-2 py-2 border-r border-slate-200 text-center">Hrs/Day</div>
                    <div className="col-span-1 px-2 py-2 border-r border-slate-200 text-center">Total Hrs</div>
                    <div className="col-span-2 px-2 py-2 border-r border-slate-200 text-right">Cost</div>
                    <div className="col-span-1 px-2 py-2 text-center">Actions</div>
                  </div>
                  {/* Data Rows */}
                  <div className="divide-y divide-slate-200">
                    {resourceAssignments.map((assignment, index) => {
                      const isEven = index % 2 === 0;
                      return (
                        <div 
                          key={assignment.id} 
                          className={`grid grid-cols-12 gap-0 items-center ${isEven ? 'bg-white' : 'bg-slate-50/50'} hover:bg-blue-50 transition-colors`}
                          data-testid={`assignment-list-item-${assignment.id}`}
                        >
                          {/* Resource Name */}
                          <div className="col-span-2 px-3 py-2 border-r border-slate-100">
                            <span className="text-sm font-medium text-slate-900">{assignment.member_name}</span>
                          </div>
                          {/* Status */}
                          <div className="col-span-1 px-2 py-2 border-r border-slate-100 text-center">
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              assignment.status === 'active' ? 'bg-green-100 text-green-700' :
                              assignment.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              assignment.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {assignment.status}
                            </span>
                          </div>
                          {/* Start Date */}
                          <div className="col-span-2 px-2 py-2 border-r border-slate-100 text-center">
                            <span className="text-xs text-slate-700">{format(new Date(assignment.assignment_start), 'dd MMM yyyy')}</span>
                          </div>
                          {/* End Date */}
                          <div className="col-span-2 px-2 py-2 border-r border-slate-100 text-center">
                            <span className="text-xs text-slate-700">{format(new Date(assignment.assignment_end), 'dd MMM yyyy')}</span>
                          </div>
                          {/* Hours/Day */}
                          <div className="col-span-1 px-2 py-2 border-r border-slate-100 text-center">
                            <span className="text-xs font-medium text-slate-700">{assignment.planned_hours_per_day}h</span>
                          </div>
                          {/* Total Hours */}
                          <div className="col-span-1 px-2 py-2 border-r border-slate-100 text-center">
                            <span className="text-xs font-medium text-slate-700">{assignment.total_planned_hours}h</span>
                          </div>
                          {/* Cost */}
                          <div className="col-span-2 px-2 py-2 border-r border-slate-100 text-right">
                            <span className="text-xs font-semibold text-slate-900">{formatCurrency(assignment.total_planned_cost)}</span>
                          </div>
                          {/* Actions */}
                          <div className="col-span-1 px-2 py-2 text-center">
                            {canEdit && (
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  onClick={() => openAssignmentModal(assignment)}
                                  className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                  title="Edit"
                                >
                                  <Edit className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteAssignment(assignment.id)}
                                  className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                  title="Remove"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* Footer with totals */}
                  <div className="bg-slate-100 border-t border-slate-300 grid grid-cols-12 gap-0 text-xs font-semibold text-slate-700">
                    <div className="col-span-2 px-3 py-2 border-r border-slate-200">
                      Total: {resourceAssignments.length} resource(s)
                    </div>
                    <div className="col-span-5 px-2 py-2 border-r border-slate-200"></div>
                    <div className="col-span-1 px-2 py-2 border-r border-slate-200 text-center">
                      {resourceAssignments.reduce((sum, a) => sum + (a.total_planned_hours || 0), 0)}h
                    </div>
                    <div className="col-span-2 px-2 py-2 border-r border-slate-200 text-right">
                      {formatCurrency(resourceAssignments.reduce((sum, a) => sum + (a.total_planned_cost || 0), 0))}
                    </div>
                    <div className="col-span-2 px-2 py-2"></div>
                  </div>
                </div>
              ) : (
                /* Grid View for Assignments */
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="assignments-grid-view">
                  {resourceAssignments.map((assignment) => (
                    <div 
                      key={assignment.id} 
                      className="border border-slate-200 rounded-xl p-4 bg-gradient-to-br from-slate-50 to-white hover:shadow-md transition-shadow"
                      data-testid={`assignment-grid-item-${assignment.id}`}
                    >
                      {/* Header with avatar and status */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${
                            assignment.status === 'active' ? 'bg-green-500' :
                            assignment.status === 'completed' ? 'bg-blue-500' :
                            assignment.status === 'cancelled' ? 'bg-red-400' :
                            'bg-slate-400'
                          }`}>
                            {assignment.member_name?.charAt(0).toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{assignment.member_name}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              assignment.status === 'active' ? 'bg-green-100 text-green-700' :
                              assignment.status === 'completed' ? 'bg-blue-100 text-blue-700' :
                              assignment.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {assignment.status}
                            </span>
                          </div>
                        </div>
                        {canEdit && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openAssignmentModal(assignment)}
                              className="p-1 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Edit"
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteAssignment(assignment.id)}
                              className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Remove"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Date Range */}
                      <div className="bg-white border border-slate-100 rounded-lg p-2 mb-3">
                        <div className="flex items-center justify-between text-xs">
                          <div className="text-center">
                            <p className="text-slate-400 uppercase tracking-wide">Start</p>
                            <p className="font-medium text-slate-700">{format(new Date(assignment.assignment_start), 'MMM dd')}</p>
                          </div>
                          <div className="text-slate-300">→</div>
                          <div className="text-center">
                            <p className="text-slate-400 uppercase tracking-wide">End</p>
                            <p className="font-medium text-slate-700">{format(new Date(assignment.assignment_end), 'MMM dd')}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Stats */}
                      <div className="grid grid-cols-2 gap-2 text-center">
                        <div className="bg-blue-50 rounded-lg p-2">
                          <p className="text-lg font-bold text-blue-700">{assignment.total_planned_hours}h</p>
                          <p className="text-xs text-blue-600">Total Hours</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-2">
                          <p className="text-lg font-bold text-green-700">{formatCurrency(assignment.total_planned_cost)}</p>
                          <p className="text-xs text-green-600">Planned Cost</p>
                        </div>
                      </div>
                      
                      {/* Hours per day */}
                      <p className="text-xs text-slate-500 text-center mt-2">
                        {assignment.planned_hours_per_day}h per day
                      </p>
                      
                      {assignment.notes && (
                        <p className="text-xs text-slate-500 mt-2 italic border-t border-slate-100 pt-2 truncate" title={assignment.notes}>
                          &ldquo;{assignment.notes}&rdquo;
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )
            ) : (
              <div className="text-center py-6 text-slate-500">
                <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>No resource assignments yet</p>
                {canEdit && (
                  <button
                    onClick={() => openAssignmentModal()}
                    className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    + Add first assignment
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Comments Section */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Comments
            </h2>
            <div className="space-y-4 mb-6">
              {comments.length > 0 ? (
                comments.map((comment) => (
                  <div key={comment.id} className="border-b border-slate-100 pb-4 last:border-0">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 text-sm font-medium">{comment.user_name.charAt(0).toUpperCase()}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900">{comment.user_name}</span>
                          <span className="text-xs text-slate-500">{format(new Date(comment.created_at), 'MMM dd, hh:mm a')}</span>
                        </div>
                        <p className="text-slate-700">{comment.comment}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 text-center py-4">No comments yet</p>
              )}
            </div>
            {canEdit ? (
              <form onSubmit={handleAddComment}>
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all mb-3"
                />
                <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg shadow-sm transition-colors">
                  Add Comment
                </button>
              </form>
            ) : (
              <p className="text-xs text-slate-400 italic">You have view-only access to this job.</p>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-1 space-y-6">
          {/* Status Update */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Update Status</h2>
            <div className="space-y-2">
              {['pending', 'in_progress', 'completed'].map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusChange(status)}
                  disabled={job.status === status || !canEdit}
                  className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                    job.status === status
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {status.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>

          {/* Job Readiness */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Job Readiness</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Materials Issued</span>
                <span className={`px-2 py-1 rounded text-sm ${job.materials_issued ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                  {job.materials_issued ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Plant/Assets Issued</span>
                <span className={`px-2 py-1 rounded text-sm ${job.plant_issued ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                  {job.plant_issued ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Sub Contractors Ready</span>
                <span className={`px-2 py-1 rounded text-sm ${job.subcontractors_ready !== false ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                  {job.subcontractors_ready !== false ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between pt-2 border-t">
                <span className="font-medium text-slate-700">Can Start</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${job.can_start ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                  {job.can_start ? 'Ready' : 'Not Ready'}
                </span>
              </div>
            </div>
          </div>

          {/* Sub Contractors */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <HardHat className="w-5 h-5" />
                Sub Contractors
              </h2>
              {canEdit && (
                <button
                  onClick={() => setShowSubcontractorModal(true)}
                  className="p-1 hover:bg-slate-100 rounded transition-colors"
                  title="Add Sub Contractor"
                >
                  <Plus className="w-5 h-5 text-blue-600" />
                </button>
              )}
            </div>
            
            {jobSubcontractors.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No sub contractors assigned</p>
            ) : (
              <div className="space-y-4">
                {jobSubcontractors.map((js) => (
                  <div key={js.id} className={`p-3 rounded-lg border ${js.documents_complete ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-slate-900">{js.subcontractor_name}</p>
                        <p className="text-xs text-slate-500">{js.contractor_type}</p>
                      </div>
                      {canEdit && (
                        <button
                          onClick={() => handleRemoveSubcontractor(js.id)}
                          className="p-1 hover:bg-red-100 rounded"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      )}
                    </div>
                    
                    {/* Contract Value */}
                    <div className="flex items-center justify-between text-sm mb-2 py-2 border-b border-slate-200">
                      <span className="text-slate-600 font-medium">Contract Value:</span>
                      {editingSubcontractorValue === js.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            defaultValue={js.contract_value || ''}
                            className="w-24 px-2 py-1 border rounded text-sm"
                            placeholder="Value"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                handleUpdateSubcontractorValue(js.id, e.target.value);
                              } else if (e.key === 'Escape') {
                                setEditingSubcontractorValue(null);
                              }
                            }}
                            autoFocus
                          />
                          <button
                            onClick={(e) => {
                              const input = e.target.closest('div').querySelector('input');
                              handleUpdateSubcontractorValue(js.id, input.value);
                            }}
                            className="text-green-600 hover:text-green-700"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingSubcontractorValue(null)}
                            className="text-slate-400 hover:text-slate-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className={js.contract_value ? 'text-slate-900 font-medium' : 'text-slate-400'}>
                            {js.contract_value ? formatCurrency(js.contract_value) : 'Not set'}
                          </span>
                          {canEdit && (
                            <button
                              onClick={() => setEditingSubcontractorValue(js.id)}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Documents Status */}
                    <div className="space-y-2 mt-3">
                      {/* Quote Document */}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Quote:</span>
                        {js.quote_document ? (
                          <div className="flex items-center gap-1">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <button
                              onClick={() => handleViewSubDoc(js.quote_document)}
                              className="text-blue-600 hover:underline text-xs"
                            >
                              View
                            </button>
                          </div>
                        ) : canEdit ? (
                          <label className="flex items-center gap-1 cursor-pointer text-orange-600 hover:text-orange-700">
                            <Upload className="w-4 h-4" />
                            <span className="text-xs">Upload</span>
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => e.target.files[0] && handleUploadQuote(js.id, e.target.files[0])}
                              disabled={uploadingSubDoc === js.id + '-quote'}
                            />
                          </label>
                        ) : (
                          <span className="text-orange-600 text-xs flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Missing
                          </span>
                        )}
                      </div>
                      
                      {/* Appointment Letter */}
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Appointment Letter:</span>
                        {js.appointment_letter ? (
                          <div className="flex items-center gap-1">
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                            <button
                              onClick={() => handleViewSubDoc(js.appointment_letter)}
                              className="text-blue-600 hover:underline text-xs"
                            >
                              View
                            </button>
                          </div>
                        ) : canEdit ? (
                          <label className="flex items-center gap-1 cursor-pointer text-orange-600 hover:text-orange-700">
                            <Upload className="w-4 h-4" />
                            <span className="text-xs">Upload</span>
                            <input
                              type="file"
                              className="hidden"
                              onChange={(e) => e.target.files[0] && handleUploadAppointmentLetter(js.id, e.target.files[0])}
                              disabled={uploadingSubDoc === js.id + '-letter'}
                            />
                          </label>
                        ) : (
                          <span className="text-orange-600 text-xs flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Missing
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {!js.documents_complete && (
                      <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Documents incomplete - Job cannot start
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Attachments */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Attachments
            </h2>
            {/* Multi-file upload section — gated on can_edit */}
            {canEdit && (
              <div className="mb-4 space-y-3">
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-blue-500 transition-colors">
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    onChange={handleAttachmentFileSelect}
                    className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {selectedAttachmentFiles.length > 0 && (
                    <p className="text-xs text-slate-500 mt-2">
                      {selectedAttachmentFiles.length} file(s) selected: {selectedAttachmentFiles.map(f => f.name).join(', ')}
                    </p>
                  )}
                </div>
                {selectedAttachmentFiles.length > 0 && (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={attachmentDescription}
                      onChange={(e) => setAttachmentDescription(e.target.value)}
                      placeholder="Description (optional - groups files)"
                      className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={handleMultiFileUpload}
                      disabled={uploading}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-sm rounded-lg transition-colors"
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      Upload
                    </button>
                  </div>
                )}
              </div>
            )}
            {uploading && <p className="text-sm text-slate-600 mb-2">Uploading...</p>}
            {job.attachments && job.attachments.length > 0 ? (
              <div className="space-y-2">
                {job.attachments.map((att, idx) => (
                  <div key={att.id || idx} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-slate-700 truncate block">{att.filename}</span>
                      {att.description && (
                        <span className="text-xs text-slate-500">{att.description}</span>
                      )}
                    </div>
                    <div className="flex gap-1 ml-2">
                      <button
                        onClick={() => handleViewAttachment(att)}
                        className="p-1 hover:bg-slate-200 rounded transition-colors"
                        title="View"
                      >
                        <Eye className="w-4 h-4 text-slate-600" />
                      </button>
                      <button
                        onClick={() => handleDownloadAttachment(att)}
                        className="p-1 hover:bg-slate-200 rounded transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4 text-slate-600" />
                      </button>
                      {canEdit && (
                        <button
                          onClick={() => handleDeleteAttachment(att.id)}
                          className="p-1 hover:bg-red-100 rounded transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center">No attachments</p>
            )}
          </div>

          {/* BOQ Summary */}
          {boq && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Bill of Quantities
              </h2>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Items</span>
                  <span className="text-slate-900">{boq.line_items?.length || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Status</span>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">{boq.status}</span>
                </div>
                <div className="flex justify-between font-medium pt-2 border-t">
                  <span className="text-slate-700">Total</span>
                  <span className="text-slate-900">{formatCurrency(boq.total_amount || 0)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Requisitions */}
          {requisitions.length > 0 && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Requisitions
              </h2>
              <div className="space-y-2">
                {requisitions.map((req, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-700">{req.items?.length || 0} items</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        req.status === 'approved' ? 'bg-green-100 text-green-700' :
                        req.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>{req.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Issued Assets */}
          {job.issued_assets && job.issued_assets.length > 0 && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600" />
                Issued Assets
                <span className="ml-auto text-sm font-normal text-slate-500">
                  {job.issued_assets.length} {job.issued_assets.length === 1 ? 'asset' : 'assets'}
                </span>
              </h2>
              <div className="space-y-3">
                {issuedAssetsDetails.length > 0 ? (
                  issuedAssetsDetails.map((asset) => (
                    <div key={asset.id} className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{asset.name}</p>
                            <p className="text-sm text-slate-600">{asset.asset_type}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          asset.condition === 'good' ? 'bg-green-100 text-green-700' :
                          asset.condition === 'fair' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {asset.condition}
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                        {asset.serial_number && (
                          <div>
                            <span className="text-slate-500">Serial:</span>
                            <span className="ml-1 font-mono text-slate-700">{asset.serial_number}</span>
                          </div>
                        )}
                        {asset.issued_to_member_name && (
                          <div>
                            <span className="text-slate-500">Responsible:</span>
                            <span className="ml-1 text-slate-700">{asset.issued_to_member_name}</span>
                          </div>
                        )}
                        {asset.expected_return_date && (
                          <div className={new Date(asset.expected_return_date) < new Date() ? 'text-red-600' : ''}>
                            <span className="text-slate-500">Expected Return:</span>
                            <span className="ml-1">{format(new Date(asset.expected_return_date), 'MMM dd, yyyy')}</span>
                            {new Date(asset.expected_return_date) < new Date() && (
                              <AlertTriangle className="w-4 h-4 inline ml-1" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-500">Loading asset details...</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sub Contractor Assignment Modal */}
      {showSubcontractorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <HardHat className="w-5 h-5" />
                Add Sub Contractor
              </h2>
              <button onClick={() => setShowSubcontractorModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="p-6">
              <p className="text-sm text-slate-600 mb-4">
                Select a sub contractor to assign to this job. You will need to upload their quote and appointment letter before the job can start.
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Select Sub Contractor</label>
                <select
                  value={selectedSubcontractor}
                  onChange={(e) => setSelectedSubcontractor(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Choose a sub contractor...</option>
                  {allSubcontractors
                    .filter(sub => !jobSubcontractors.some(js => js.subcontractor_id === sub.id))
                    .map((sub) => (
                      <option key={sub.id} value={sub.id}>
                        {sub.name} - {sub.contractor_type}
                      </option>
                    ))}
                </select>
              </div>

              {selectedSubcontractor && (
                <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                  {(() => {
                    const sub = allSubcontractors.find(s => s.id === selectedSubcontractor);
                    return sub ? (
                      <div className="space-y-1 text-sm">
                        <p className="font-medium text-slate-900">{sub.name}</p>
                        <p className="text-slate-600">{sub.contractor_type}</p>
                        <p className="text-slate-600">Contact: {sub.contact_person}</p>
                        <p className="text-slate-600">{sub.contact_number}</p>
                        <div className="pt-2">{renderStars(sub.rating)}</div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              {selectedSubcontractor && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 mb-2">Contract Value (R)</label>
                  <input
                    type="text"
                    value={subcontractorContractValue}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setSubcontractorContractValue(value);
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter contract value"
                  />
                  <p className="text-xs text-slate-500 mt-1">This value will be used for cashflow tracking</p>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowSubcontractorModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignSubcontractor}
                  disabled={!selectedSubcontractor}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Assign Sub Contractor
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resource Assignment Modal */}
      {showAssignmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900">
                  {editingAssignment ? 'Edit Resource Assignment' : 'Add Resource Assignment'}
                </h3>
                <button onClick={() => setShowAssignmentModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-700">
                    {editingAssignment ? 'Team Member' : 'Select Team Members *'}
                    {!editingAssignment && selectedMemberIds.length > 0 && (
                      <span className="ml-2 text-xs text-blue-600 font-normal">
                        ({selectedMemberIds.length} selected)
                      </span>
                    )}
                  </label>
                </div>
                
                {editingAssignment ? (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="font-medium text-slate-900">{editingAssignment.member_name}</p>
                    <p className="text-sm text-slate-500">Team member cannot be changed when editing</p>
                  </div>
                ) : (
                  /* Multi-select Listbox */
                  <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="resource-multiselect-list">
                    {/* Header */}
                    <div className="bg-slate-100 border-b border-slate-200 px-3 py-2 flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Available Team Members</span>
                      {selectedMemberIds.length > 0 && (
                        <button
                          onClick={() => setSelectedMemberIds([])}
                          className="text-xs text-slate-500 hover:text-slate-700"
                        >
                          Clear all
                        </button>
                      )}
                    </div>
                    {/* List */}
                    <div className="max-h-48 overflow-y-auto">
                      {teamMembersList.length === 0 ? (
                        <p className="p-4 text-center text-slate-500 text-sm">No team members available</p>
                      ) : (
                        teamMembersList.map((member, index) => {
                          const isSelected = selectedMemberIds.includes(member.id);
                          const isEven = index % 2 === 0;
                          return (
                            <div
                              key={member.id}
                              onClick={() => toggleMemberSelection(member.id)}
                              className={`px-3 py-2 cursor-pointer transition-colors flex items-center gap-3 ${
                                isSelected 
                                  ? 'bg-blue-50' 
                                  : isEven ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-100'
                              }`}
                              data-testid={`resource-multiselect-item-${member.id}`}
                            >
                              {/* Checkbox */}
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                                isSelected 
                                  ? 'bg-blue-600 border-blue-600' 
                                  : 'border-slate-300 bg-white'
                              }`}>
                                {isSelected && (
                                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                )}
                              </div>
                              {/* Member Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {member.employee_id && (
                                    <span className="text-xs font-mono text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                      {member.employee_id}
                                    </span>
                                  )}
                                  <span className="font-medium text-slate-900 text-sm truncate">{member.name}</span>
                                </div>
                                <p className="text-xs text-slate-500">
                                  {member.role} {member.hourly_rate > 0 && `• R${member.hourly_rate}/hr`}
                                </p>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                    {/* Footer with selection summary */}
                    {selectedMemberIds.length > 0 && (
                      <div className="bg-blue-50 border-t border-blue-100 px-3 py-2">
                        <p className="text-xs text-blue-700">
                          <span className="font-medium">{selectedMemberIds.length}</span> team member(s) will be assigned with the same schedule
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={assignmentForm.assignment_start}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_start: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={assignmentForm.assignment_start_time}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_start_time: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">End Date *</label>
                  <input
                    type="date"
                    value={assignmentForm.assignment_end}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_end: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">End Time</label>
                  <input
                    type="time"
                    value={assignmentForm.assignment_end_time}
                    onChange={(e) => setAssignmentForm({ ...assignmentForm, assignment_end_time: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <p className="text-xs text-slate-500">
                Tip: For single-day assignments, set the same date with different start/end times.
              </p>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Hours per Day: {assignmentForm.planned_hours_per_day}
                </label>
                <input
                  type="range"
                  min="1"
                  max="12"
                  step="0.5"
                  value={assignmentForm.planned_hours_per_day}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, planned_hours_per_day: parseFloat(e.target.value) })}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>1h</span>
                  <span>12h</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={assignmentForm.notes}
                  onChange={(e) => setAssignmentForm({ ...assignmentForm, notes: e.target.value })}
                  placeholder="Optional notes about this assignment..."
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="text-slate-600">
                  <AlertTriangle className="w-4 h-4 inline mr-1 text-amber-500" />
                  Assignments will check for scheduling conflicts to prevent double-booking team members.
                </p>
              </div>
              
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowAssignmentModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAssignment}
                  disabled={
                    (editingAssignment 
                      ? !assignmentForm.member_id 
                      : selectedMemberIds.length === 0
                    ) || !assignmentForm.assignment_start || !assignmentForm.assignment_end
                  }
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg transition-colors"
                  data-testid="save-assignment-btn"
                >
                  {editingAssignment ? 'Update Assignment' : 'Create Assignment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gallery Modal */}
      <GalleryModal
        isOpen={showGalleryModal}
        onClose={() => setShowGalleryModal(false)}
        jobId={id}
        jobCode={job?.job_code || job?.title}
        canEdit={canEdit}
      />

      {/* Program Module Modal */}
      <ProgramModal
        isOpen={showProgramModal}
        onClose={() => setShowProgramModal(false)}
        jobId={id}
        jobCode={job?.job_code || job?.title}
        canEdit={canEdit}
      />
    </div>
  );
};

export default JobDetail;
